import { useCallback, useEffect, useMemo, useState } from 'react';
import { createBackend } from '#ops-impl';
import { DEMO_USERS, can, type Permission, type PortalUser } from '@urbanrats/ops-core';
import type { OpsBackend } from './backend/types.ts';
import { FilterBar } from './components/FilterBar.tsx';
import { Link } from './components/Link.tsx';
import { AppCtx, STALE_AFTER_MS, currentFilters, fmtTime, hrefWith, useLocation, type Ctx } from './lib.ts';
import { Overview } from './pages/Overview.tsx';
import { Cameras, CameraDetail } from './pages/Cameras.tsx';
import { Rounds, RoundDetail } from './pages/Rounds.tsx';
import { Bets, BetDetail, Transactions } from './pages/Bets.tsx';
import { Players, PlayerDetail } from './pages/Players.tsx';
import { Operators } from './pages/Operators.tsx';
import { Risk } from './pages/Risk.tsx';
import { Promotions } from './pages/Promotions.tsx';
import { Finance, Reconciliation } from './pages/Finance.tsx';
import { Audit } from './pages/Audit.tsx';
import { System } from './pages/System.tsx';
import { Developer } from './pages/Developer.tsx';
import { CameraPipeline } from './pages/CameraPipeline.tsx';
import { Pricing } from './pages/ServerReports.tsx';
import { Labelling } from './pages/Labelling.tsx';
import { CommandCenter } from './pages/CommandCenter.tsx';
import { CommandPalette } from './components/CommandPalette.tsx';
import { Icon, type IconName } from './components/Icon.tsx';
import { navigate } from './lib.ts';
import { SignIn } from './components/SignIn.tsx';
import { Users } from './pages/Users.tsx';
import { Account } from './pages/Account.tsx';
import { Studio } from './pages/Studio.tsx';
import { Notifications } from './components/Notifications.tsx';
import { Wall } from './pages/Wall.tsx';
import { Voids } from './pages/Voids.tsx';
import { TeamPanel, useTeamHeartbeat } from './components/Team.tsx';
import { PageHead } from './components/common.tsx';
import { Engagement } from './pages/Engagement.tsx';

const USER_KEY = 'urbanrats.admin.user.v1';

export const NAV: { path: string; label: string; perm: Permission; group: string; icon: IconName; apiOnly?: boolean }[] = [
  { path: '/live', label: 'Live operations', perm: 'view.rounds', group: 'Live', icon: 'live', apiOnly: true },
  { path: '/', label: 'Overview', perm: 'view.overview', group: 'Live', icon: 'overview' },
  { path: '/team', label: 'Team', perm: 'view.overview', group: 'Live', icon: 'chat', apiOnly: true },
  { path: '/wall', label: 'Video wall', perm: 'view.rounds', group: 'Live', icon: 'camera', apiOnly: true },
  { path: '/rounds', label: 'Rounds', perm: 'view.rounds', group: 'Live', icon: 'rounds' },
  { path: '/voids', label: 'Void analysis', perm: 'view.rounds', group: 'Live', icon: 'alert', apiOnly: true },
  { path: '/cameras', label: 'Cameras', perm: 'view.cameras', group: 'Cameras', icon: 'camera' },
  { path: '/studio', label: 'Camera studio', perm: 'view.cameras', group: 'Cameras', icon: 'globe', apiOnly: true },
  { path: '/camera-pipeline', label: 'Camera pipeline', perm: 'view.cameras', group: 'Cameras', icon: 'pipeline' },
  { path: '/labelling', label: 'Count labelling', perm: 'view.cameras', group: 'Cameras', icon: 'label' },
  { path: '/bets', label: 'Bets', perm: 'view.bets', group: 'Players & money', icon: 'bets' },
  { path: '/transactions', label: 'Wallet transactions', perm: 'view.bets', group: 'Players & money', icon: 'wallet' },
  { path: '/players', label: 'Players', perm: 'view.players', group: 'Players & money', icon: 'players' },
  { path: '/finance', label: 'Finance', perm: 'view.finance', group: 'Players & money', icon: 'finance' },
  { path: '/reconciliation', label: 'Reconciliation', perm: 'view.reconciliation', group: 'Players & money', icon: 'reconcile' },
  { path: '/risk', label: 'Risk', perm: 'view.risk', group: 'Trading', icon: 'risk' },
  { path: '/pricing', label: 'Pricing', perm: 'view.risk', group: 'Trading', icon: 'pricing' },
  { path: '/promotions', label: 'Promotions', perm: 'view.promotions', group: 'Trading', icon: 'promo' },
  { path: '/engagement', label: 'Rewards & leaderboards', perm: 'view.promotions', group: 'Trading', icon: 'bolt', apiOnly: true },
  { path: '/operators', label: 'Operators & brands', perm: 'view.operators', group: 'Partners', icon: 'operators' },
  { path: '/developer', label: 'Developer', perm: 'view.developer', group: 'Partners', icon: 'code' },
  { path: '/audit', label: 'Audit', perm: 'view.audit', group: 'Governance', icon: 'audit' },
  { path: '/system', label: 'System', perm: 'view.system', group: 'Governance', icon: 'system' },
  { path: '/users', label: 'Users & roles', perm: 'manage.users', group: 'Governance', icon: 'players', apiOnly: true },
];

function loadUser(): PortalUser {
  try {
    const id = localStorage.getItem(USER_KEY);
    return DEMO_USERS.find((u) => u.userId === id) ?? DEMO_USERS[0]!;
  } catch {
    return DEMO_USERS[0]!;
  }
}

export function App() {
  const [backend, setBackend] = useState<OpsBackend | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    createBackend().then(
      (b) => live && setBackend(b),
      (e: unknown) => live && setFailure(e instanceof Error ? e.message : String(e)),
    );
    return () => {
      live = false;
    };
  }, [attempt]);
  if (failure)
    return (
      <main className="state-page" role="alert" data-testid="load-error">
        <h1>UrbanRats operations</h1>
        <p>Could not load operations data: {failure}</p>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setFailure(null);
            const u = new URL(location.href);
            u.searchParams.delete('demoFail');
            history.replaceState(null, '', u);
            setAttempt((n) => n + 1);
          }}
        >
          Retry
        </button>
      </main>
    );
  if (!backend)
    return (
      <main className="state-page" aria-busy="true" data-testid="loading">
        <h1>UrbanRats operations</h1>
        <p className="muted">Loading operations data…</p>
      </main>
    );
  return <Signed backend={backend} onRebuild={() => (setBackend(null), setAttempt((n) => n + 1))} />;
}

/** API mode: real sign-in (password + authenticator code, enrolment at first sign-in) before anything is loaded. */
function Signed({ backend, onRebuild }: { backend: OpsBackend; onRebuild: () => void }) {
  const [user, setUser] = useState<PortalUser | null>(backend.auth.mode === 'login' ? backend.auth.current() : null);
  const [ended, setEnded] = useState(false);
  useEffect(() => {
    const onEnd = () => {
      setUser(null);
      setEnded(true);
    };
    window.addEventListener('urbanrats:admin-session-ended', onEnd);
    return () => window.removeEventListener('urbanrats:admin-session-ended', onEnd);
  }, []);
  if (backend.auth.mode === 'fixtures') return <Shell backend={backend} onRebuild={onRebuild} />;
  if (user) return <Shell backend={backend} onRebuild={onRebuild} fixedUser={user} onSignOut={() => (sessionStorage.clear(), setUser(null))} />;
  return (
    <SignIn
      auth={backend.auth}
      notice={ended ? 'Your session has ended (sessions last 8 hours). Sign in again to continue — nothing on the server stopped.' : null}
      onDone={(u) => {
        setEnded(false);
        setUser(u);
      }}
    />
  );
}

function Shell({ backend, onRebuild, fixedUser, onSignOut }: { backend: OpsBackend; onRebuild: () => void; fixedUser?: PortalUser; onSignOut?: () => void }) {
  const { path, search } = useLocation();
  const [picked, setUserState] = useState<PortalUser>(loadUser);
  const user = fixedUser ?? picked;
  const [rev, setRev] = useState(0);
  const [nowMs, setNow] = useState(() => Date.now());
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);
  // Real backend: keep the snapshot fresh (the live page has its own faster read model).
  useEffect(() => {
    if (backend.kind !== 'api') return;
    const t = setInterval(() => void backend.refresh().then(() => setRev((n) => n + 1)), 30_000);
    return () => clearInterval(t);
  }, [backend]);
  const refresh = useCallback(() => {
    void backend.refresh().then(() => setRev((n) => n + 1));
    setRev((n) => n + 1);
  }, [backend]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- rev forces a re-read after mutations
  const data = useMemo(() => backend.data(user), [backend, user, rev]);
  const filters = currentFilters(search);
  const ctx: Ctx = { backend, user, data, filters, search, refresh, nowMs };
  const stale = nowMs - Date.parse(data.asOf) > STALE_AFTER_MS;
  const setUser = (u: PortalUser) => {
    setUserState(u);
    try {
      localStorage.setItem(USER_KEY, u.userId);
    } catch {
      /* demo convenience */
    }
  };
  // Presence for the team area (staff only): heartbeat while the portal is open; counts feed the Team badge.
  const team = useTeamHeartbeat(backend.server, backend.kind === 'api' && user.role !== 'PARTNER_ADMIN');
  const nav = NAV.filter((n) => can(user, n.perm) && (!n.apiOnly || !!backend.server) && (n.path !== '/team' || user.role !== 'PARTNER_ADMIN'));
  const section = '/' + (path.split('/')[1] ?? '');
  const current = NAV.find((n) => n.path === section);
  const allowed = current ? can(user, current.perm) : true;
  const groups = [...new Set(nav.map((n) => n.group))];
  const [palette, setPalette] = useState(false);
  // Signed in on the real backend: start on the live command center.
  useEffect(() => {
    if (backend.server && path === '/' && !search && can(user, 'view.rounds')) navigate('/live', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per sign-in
  }, []);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette((v) => !v);
      }
    };
    addEventListener('keydown', k);
    return () => removeEventListener('keydown', k);
  }, []);
  const initials = user.displayName
    .replace(/\(.*\)/, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

  return (
    <AppCtx.Provider value={ctx}>
      <div className="shell">
        <header className="top">
          <button type="button" className="icon-btn menu-btn" aria-expanded={menuOpen} aria-controls="side-nav" aria-label="Menu" onClick={() => setMenuOpen(!menuOpen)}>
            <Icon name="menu" />
          </button>
          <div className="crumbs">
            <span className="sub">{current?.group ?? 'Operations'}</span>
            <span className="sep">/</span>
            <span>{current?.label ?? 'Operations'}</span>
          </div>
          <span className="spacer" />
          <button type="button" className="search-btn" onClick={() => setPalette(true)} data-testid="palette-open">
            <Icon name="search" size={16} />
            <span className="label">Search rounds, cameras, pages…</span>
            <kbd>⌘K</kbd>
          </button>
          {backend.kind === 'demo' ? (
            <span className="badge warn" data-testid="demo-badge" title="Browser-side role fixtures only">
              DEMO · RBAC in the browser only — server authorization not built (P10)
            </span>
          ) : (
            <span className="badge env" data-testid="api-badge" title="Server-authorised session: RBAC, tenant scope and audit enforced by the API">
              ● Server session · RBAC enforced
            </span>
          )}
          <span className={`badge ${stale ? 'warn' : ''}`} data-testid="as-of" title={`Data as of ${fmtTime(data.asOf)}`}>
            <Icon name="clock" size={12} className="inline-icon" /> {fmtTime(data.asOf).replace(/^[A-Z][a-z]{2} \d+, \d{4}, /, '')}
            {stale && <strong data-testid="stale"> · STALE (snapshot, not live)</strong>}
          </span>
          {stale && (
            <button type="button" className="btn ghost" onClick={onRebuild} title="Rebuilds the demo snapshot; demo changes in this tab are lost">
              Rebuild snapshot
            </button>
          )}
          {backend.server && fixedUser && <Notifications server={backend.server} />}
          {fixedUser ? (
            <span className="user-chip" data-testid="signed-in">
              <Link to="/account" className="avatar" testId="account-link">
                <span aria-hidden>{initials}</span>
                <span className="sr-only">My account</span>
              </Link>
              <span className="who">
                <strong>
                  <span className="sr-only">Signed in as </span>
                  {fixedUser.displayName.replace(/ \(demo\)$/, '')}
                </strong>
                <span>{fixedUser.role.replace(/_/g, ' ').toLowerCase()}</span>
              </span>
              <button type="button" className="icon-btn" onClick={onSignOut} aria-label="Sign out" title="Sign out">
                <Icon name="logout" size={16} />
              </button>
            </span>
          ) : (
            <label className="user-switch">
              <span>Signed in as (demo)</span>
              <select value={user.userId} onChange={(e) => setUser(DEMO_USERS.find((u) => u.userId === e.target.value)!)} data-testid="user-switch">
                {DEMO_USERS.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}
        </header>
        <div className="body">
          <nav id="side-nav" className={`side ${menuOpen ? 'open' : ''}`} aria-label="Sections">
            <div className="side-brand" aria-hidden>
              <span className="mark">◆</span>
              <span>
                UrbanRats
                <small>operations</small>
              </span>
            </div>
            {groups.map((g) => (
              <div key={g} role="group" aria-label={g}>
                <div className="side-group" aria-hidden>
                  {g}
                </div>
                {nav
                  .filter((n) => n.group === g)
                  .map((n) => (
                    <Link key={n.path} to={n.path === '/live' ? '/live' : hrefWith(n.path, filters)} className={section === n.path ? 'active' : ''}>
                      <Icon name={n.icon} />
                      {n.label}
                      {n.path === '/live' && <span className="live-dot" aria-hidden />}
                      {n.path === '/team' && team && team.unreadTotal > 0 && <span className="tm-blink" title={`${team.unreadTotal} new message(s)`} aria-label={`${team.unreadTotal} new message(s)`} role="img" />}
                      {n.path === '/team' && team && team.unreadTotal + team.myOpenTasks > 0 && (
                        <span className={`tm-badge nav ${team.myUrgentTasks ? 'urgent' : ''}`} title={`${team.unreadTotal} unread · ${team.myOpenTasks} open task(s) for you`}>
                          {team.unreadTotal + team.myOpenTasks}
                        </span>
                      )}
                    </Link>
                  ))}
              </div>
            ))}
            {user.role === 'PARTNER_ADMIN' && <p className="note">Partner view: only {data.tenants[0]?.operatorName ?? user.operatorId} data is loaded.</p>}
            <div className="side-foot">Times in UTC · money per currency, never summed across currencies</div>
          </nav>
          <main className="content" onClick={() => setMenuOpen(false)}>
            {allowed ? (
              <>
                {!['/audit', '/system', ...(backend.server ? ['/reconciliation'] : []), '/operators', '/developer', '/camera-pipeline', '/pricing', '/labelling', '/live', '/users', '/account', '/studio', '/wall', '/voids', '/team', '/engagement'].includes(section) && <FilterBar path={path} />}
                <Route path={path} />
              </>
            ) : (
              <p className="empty" role="alert" data-testid="forbidden">
                Your role ({user.role}) cannot open this section.
              </p>
            )}
          </main>
        </div>
        {palette && <CommandPalette nav={nav} onClose={() => setPalette(false)} />}
      </div>
    </AppCtx.Provider>
  );
}

function Route({ path }: { path: string }) {
  const [, a, b] = path.split('/').map(decodeURIComponent);
  switch (a) {
    case '':
      return <Overview />;
    case 'cameras':
      return b ? <CameraDetail id={b} /> : <Cameras />;
    case 'rounds':
      return b ? <RoundDetail id={b} /> : <Rounds />;
    case 'bets':
      return b ? <BetDetail betKey={path.slice('/bets/'.length)} /> : <Bets />;
    case 'transactions':
      return <Transactions />;
    case 'players':
      return b ? <PlayerDetail id={b} /> : <Players />;
    case 'operators':
      return <Operators />;
    case 'risk':
      return <Risk />;
    case 'promotions':
      return <Promotions />;
    case 'finance':
      return <Finance />;
    case 'reconciliation':
      return <Reconciliation />;
    case 'audit':
      return <Audit />;
    case 'system':
      return <System />;
    case 'developer':
      return <Developer />;
    case 'camera-pipeline':
      return <CameraPipeline />;
    case 'pricing':
      return <Pricing />;
    case 'labelling':
      return <Labelling />;
    case 'live':
      return <CommandCenter />;
    case 'users':
      return <Users />;
    case 'studio':
      return <Studio />;
    case 'team':
      return <TeamPage />;
    case 'engagement':
      return <Engagement />;
    case 'wall':
      return <Wall />;
    case 'voids':
      return <Voids />;
    case 'account':
      return <Account />;
    default:
      return <p className="empty">Page not found.</p>;
  }
}

function TeamPage() {
  return (
    <>
      <PageHead title="Team">Who is on, in which role and status; channels, direct messages and tasks. Admins can read the full log.</PageHead>
      <TeamPanel />
    </>
  );
}
